import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import dayjs, { Dayjs } from "dayjs";
import { addMonths, endOfDay, startOfDay } from "date-fns";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchEmployees,
  selectProcesses,
} from "../../features/process/processSlices";
import Grid from "@mui/material/Grid";

import {
  changePageTitle,
  selectPreference,
} from "../../features/preferences/preferencesSlice";
import {
  Typography,
  Box,
  Accordion,
  AccordionDetails,
  TextField,
  MenuItem,
  Button,
  Stack,
  CircularProgress,
} from "@mui/material";
import { ExpandMore, Search, ShowChart } from "@mui/icons-material";

import { useTranslation } from "react-i18next";
import { PDFViewer } from "@react-pdf/renderer";
import { selectAuth } from "../../features/auth/authSlice";
import { EmployeeStatus } from "../../features/process/types/processTypes";
import { StyledAccordionSummary } from "../../styles/componentStyled";
import {
  getAmharicCalendar,
  getAmharicCalendarFormatted,
} from "../../utils/calendarUtility";
import { AfterFlight } from "./labour/AfterFlight";
import { getEmployeeStatusDate } from "../../features/process/utils/statusDate";

const REPORT_STATUS_OPTIONS: EmployeeStatus[] = [
  EmployeeStatus.departed,
  EmployeeStatus.lost,
  EmployeeStatus.returned,
  EmployeeStatus.discontinued,
  EmployeeStatus.contract_completed,
];

const MIN_DATE = new Date("2023-01-01");

function pickerValueToDate(value: Dayjs | Date | null | undefined): Date | null {
  if (value == null) return null;
  if (dayjs.isDayjs(value)) return value.toDate();
  return value instanceof Date ? value : dayjs(value).toDate();
}

function isDateInRangeInclusive(
  date: Date,
  rangeStart: Date,
  rangeEnd: Date,
): boolean {
  const d = date.getTime();
  return (
    d >= startOfDay(rangeStart).getTime() && d <= endOfDay(rangeEnd).getTime()
  );
}

export const Reports = () => {
  const dispatch = useAppDispatch();
  const { t: te } = useTranslation("enums");
  const { t } = useTranslation();
  const {
    employeesWithCount: { employees },
    loading,
  } = useAppSelector(selectProcesses);
  const { locale } = useAppSelector(selectPreference);
  const { user } = useAppSelector(selectAuth);

  const [status, setStatus] = useState(EmployeeStatus.departed);
  const [startDate, setStartDate] = useState<Date | null>(
    addMonths(new Date(), -1),
  );
  const [durationEndDate, setDurationEndDate] = useState<Date | null>(
    addMonths(new Date(), 1),
  );
  const [letterDate, setLetterDate] = useState<Date | null>(new Date());
  const [letterNumber, setLetterNumber] = useState("");
  const [showReport, setShowReport] = useState(false);

  const startAmharicDate = startDate
    ? getAmharicCalendar(startDate, true)
    : "";
  const endAmharicDate = durationEndDate
    ? getAmharicCalendar(durationEndDate, true)
    : "";
  const letterAmharicDate = letterDate
    ? getAmharicCalendarFormatted(letterDate, "/")
    : "";

  useEffect(() => {
    dispatch(changePageTitle("Reports"));
  }, [dispatch]);

  // Hide the report whenever the user changes filter inputs so they must re-fetch
  useEffect(() => {
    setShowReport(false);
  }, [status, startDate, durationEndDate]);

  const isFetching = loading === "pending";

  const filteredEmployees = useMemo(() => {
    if (!startDate || !durationEndDate) return [];
    return employees.filter((emp) => {
      if (emp.status !== status) return false;
      const statusDate = getEmployeeStatusDate(emp);
      return !!(
        statusDate &&
        isDateInRangeInclusive(
          new Date(statusDate as Date),
          startDate,
          durationEndDate,
        )
      );
    });
  }, [employees, status, startDate, durationEndDate]);

  const handleShow = () => {
    dispatch(
      fetchEmployees({
        status,
        skip: 0,
        take: 2000,
        refreshList: "network-only",
      }),
    );
    setShowReport(true);
  };

  return (
    <>
      <Helmet>
        <title>{`${t("reports")} | ${t("platform")}`}</title>
      </Helmet>
      <Box sx={{ m: { xs: 0, md: 1 }, px: { xs: 0, md: 2 } }}>
        <Accordion expanded>
          <StyledAccordionSummary
            expandIcon={<ExpandMore />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <Search />
            <Typography variant="h6" component="div">
              {t("filterReport")}
            </Typography>
          </StyledAccordionSummary>

          <AccordionDetails>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={locale}
            >
              <Grid
                container
                spacing={2}
                alignItems="flex-start"
                justifyContent="center"
              >
                <Grid item md={4} xs={12}>
                  <TextField
                    id="statuses"
                    select
                    label={t("byCurrentStatus")}
                    onChange={(e) =>
                      setStatus(e.target.value as EmployeeStatus)
                    }
                    value={status}
                    variant="outlined"
                    size="small"
                    fullWidth
                    sx={{ mt: 1 }}
                  >
                    {REPORT_STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {te(option.toLowerCase())}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item md={4} xs={12}>
                  <Stack direction="row" alignItems="center">
                    <DatePicker
                      label={t("fromDate")}
                      disableMaskedInput
                      views={["year", "month", "day"]}
                      minDate={MIN_DATE}
                      inputFormat="DD-MMM-YYYY"
                      value={startDate ? dayjs(startDate) : null}
                      onChange={(newValue) =>
                        setStartDate(pickerValueToDate(newValue))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          id="reportStartDate"
                          sx={{ mt: 1 }}
                          fullWidth
                          size="small"
                        />
                      )}
                    />
                    <Typography variant="h6" noWrap sx={{ width: "60%" }}>
                      {startAmharicDate}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item md={4} xs={12}>
                  <Stack direction="row" alignItems="center">
                    <DatePicker
                      label={t("toDate")}
                      disableMaskedInput
                      views={["year", "month", "day"]}
                      minDate={MIN_DATE}
                      inputFormat="DD-MMM-YYYY"
                      value={durationEndDate ? dayjs(durationEndDate) : null}
                      onChange={(newValue) =>
                        setDurationEndDate(pickerValueToDate(newValue))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          id="durationEndDate"
                          sx={{ mt: 1 }}
                          fullWidth
                          size="small"
                        />
                      )}
                    />
                    <Typography variant="h6" noWrap sx={{ width: "60%" }}>
                      {endAmharicDate}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid item md={4} xs={12}>
                  <Stack direction="row" alignItems="center">
                    <DatePicker
                      label={"ቀን"}
                      disableMaskedInput
                      views={["year", "month", "day"]}
                      minDate={MIN_DATE}
                      inputFormat="DD-MMM-YYYY"
                      value={letterDate ? dayjs(letterDate) : null}
                      onChange={(newValue) =>
                        setLetterDate(pickerValueToDate(newValue))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          id="letterDate"
                          sx={{ mt: 1 }}
                          fullWidth
                          size="small"
                        />
                      )}
                    />
                    <Typography variant="h6" noWrap sx={{ width: "60%" }}>
                      {letterAmharicDate}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item md={4} xs={12}>
                  <TextField
                    id="filterByText"
                    label="ደብዳቤ ቁጥር"
                    onChange={(e) => setLetterNumber(e.target.value)}
                    value={letterNumber}
                    variant="outlined"
                    size="small"
                    fullWidth
                    sx={{ mt: 1 }}
                  />
                </Grid>
                <Grid item md={4} xs={12}>
                  <Button
                    color="secondary"
                    variant="contained"
                    onClick={handleShow}
                    disabled={isFetching}
                    size="large"
                    fullWidth
                    sx={{ mt: 1 }}
                  >
                    {isFetching ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      <>
                        <ShowChart /> {t("show")}
                      </>
                    )}
                  </Button>
                </Grid>
              </Grid>
            </LocalizationProvider>
          </AccordionDetails>
        </Accordion>

        {showReport && !isFetching && filteredEmployees.length > 0 && user && (
          <PDFViewer style={{ height: "900px", width: "100%" }}>
            <AfterFlight
              employees={filteredEmployees}
              user={user}
              letterDate={letterAmharicDate}
              letterNumber={letterNumber}
              reportType={status}
              durationStartDate={startAmharicDate}
              durationEndDate={endAmharicDate}
            />
          </PDFViewer>
        )}
      </Box>
    </>
  );
};
